import React, { useMemo, useState } from 'react';
import { Box } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent } from '../hooks';
import PlaceholderBase from '../PlaceholderBase';
import PlanYearSelector from '../PlanYearSelector';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import { ReportSummaryBase, ReportSummaryWrapper } from '../ReportSummary';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { ForfeitureSummaryReportParams } from './ForfeitureReport.types';
import { useForfeitureReportExportSummaryCSVQuery, useForfeitureReportSummaryQuery } from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: ForfeitureSummaryReportParams;
  onSetFilterValue: FilterSetters<ForfeitureSummaryReportParams>;
}

const ForfeitureReportSummary: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues);
  const [isAutoFocus, setAutoFocus] = useState(false);

  const { isLoading, data, lastTimeUpdateDate, title, isEmptyData, isLoadingMore, isHasNextPage, handleLoadMore } = useForfeitureReportSummaryQuery({
    isWithTotal: true,
    ...filtersValues,
  });

  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useForfeitureReportExportSummaryCSVQuery({
      ...filtersValues,
    });

  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }
    if (!isLoading && !data.length) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }

    return (
      <ReportSummaryBase isPreviewMode={isPreviewMode} subTitle={title} isLoading={isLoading} data={data} onLoadMore={handleLoadMore} isHasMore={isHasNextPage} isLoadingMore={isLoadingMore} />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtersValues.employerId, filtersValues.employerIds, isLoading, isPreviewMode, title]);
  return (
    <TopButtonPortalWrapper
      isPreviewMode={isPreviewMode}
      infoNode={
        <ReportLastUpdateDate
          isLoading={isLoading}
          key={`${filtersValues.employerId}`}
          value={lastTimeUpdateDate}
        />
      }
      buttonNode={
        <Box direction="row">
          <AddScheduledReportButton
            employerId={filtersValues.employerId}
            type={ReportTypeKeys.Forfeiture}
            tab={ReportTab.Summary}
          />
          <Box margin={{ left: 'spacing12' }}>
            <ExportButton
              onClick={handleExportCSV}
              isLoading={isExportLoading}
              disabled={!isEmployerIsPresent || isEmptyData}
              text="Download Summary (CSV)"
            />
          </Box>
        </Box>
      }
    >
      <ReportSummaryWrapper
        isPreviewMode={isPreviewMode}
        filters={
          <Box fill direction="row">
            <Box width="290px">
              <EmployerSelector
                disabled={isPreviewMode && !!defaultEmployerId}
                onSetAutoFocus={setAutoFocus}
                isAutoFocus={isAutoFocus}
                value={filtersValues.employerId}
                onChangeValue={onSetFilterValue.employerId}
              />
            </Box>

            <Box width="290px" margin={{ left: 'spacing12' }}>
              <PlanYearSelector
                disabled={!isEmployerIsPresent}
                value={filtersValues.planYearPeriod}
                onChangeValue={onSetFilterValue.planYearPeriod}
              />
            </Box>
          </Box>
        }
      >
        {childrenNode}
      </ReportSummaryWrapper>
    </TopButtonPortalWrapper>
  );
};

export default ForfeitureReportSummary;
